import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowUpRight, Clock, Users, Calendar, Award, ArrowLeft } from "lucide-react";
import SectionLabel from "@/components/SectionLabel";
import usePageMeta from "@/hooks/usePageMeta";
import { trainingenData, referenties } from "@/data/trainingen";
import { buildPilotMailto } from "@/lib/mailto";

export default function TrainingDetail() {
  const { key } = useParams();
  const training = trainingenData.find((t) => t.key === key);
  usePageMeta({
    title: training ? `${training.titel}, Training LevelLeven` : "Training, LevelLeven",
    description: training?.intro?.slice(0, 155),
  });

  if (!training) return <Navigate to="/trainingen" replace />;

  return (
    <div>
      <section className="py-16 sm:py-24">
        <div className="max-w-[1200px] mx-auto px-6 sm:px-10 lg:px-16">
          <Link
            to="/trainingen"
            className="inline-flex items-center gap-2 text-sm text-[#7A8276] hover:text-[#1D2A22] mb-8 transition-colors"
          >
            <ArrowLeft size={14} /> Alle trainingen
          </Link>
          <SectionLabel>Training · LevelLeven-methodiek</SectionLabel>
          <h1 className="font-heading text-5xl sm:text-6xl leading-[1.05] tracking-tight text-[#1D2A22] font-light">
            {training.titel}
          </h1>
          <p className="mt-4 text-[#7A8276] italic">{training.subtitel}</p>
          <p className="mt-8 text-lg text-[#4A5D4E] leading-relaxed font-light max-w-3xl">
            {training.intro}
          </p>
        </div>
      </section>

      <section className="pb-16">
        <div className="max-w-[1200px] mx-auto px-6 sm:px-10 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-7 space-y-10">
            <div>
              <p className="text-xs uppercase tracking-[0.25em] text-[#E06B3F] mb-4">Wat leert u concreet?</p>
              <ul className="space-y-3">
                {training.leerdoelen.map((l, i) => (
                  <li key={i} className="flex items-baseline gap-3 text-[#1D2A22] text-lg">
                    <span className="text-xs uppercase tracking-[0.25em] text-[#E06B3F] w-8 shrink-0">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    {l}
                  </li>
                ))}
              </ul>
            </div>
            <div className="border-t border-[#E5E1D8] pt-8">
              <p className="text-xs uppercase tracking-[0.25em] text-[#E06B3F] mb-4">Resultaat</p>
              <p className="text-lg text-[#4A5D4E] leading-relaxed font-light">{training.resultaat}</p>
            </div>
            <div className="border-t border-[#E5E1D8] pt-8">
              <p className="text-xs uppercase tracking-[0.25em] text-[#E06B3F] mb-4">Wetenschappelijke onderbouwing</p>
              <ol className="space-y-3 text-sm text-[#4A5D4E] leading-relaxed">
                {training.referenties.map((r, i) => (
                  <li key={r}>
                    <span className="text-[#7A8276] mr-2">{String(i + 1).padStart(2, "0")}</span>
                    {referenties[r]}
                  </li>
                ))}
              </ol>
            </div>
          </div>

          <aside className="lg:col-span-5">
            <div className="rounded-3xl bg-[#EFECE5] border border-[#E5E1D8] p-6 sm:p-8 lg:sticky lg:top-28">
              <p className="text-xs uppercase tracking-[0.25em] text-[#E06B3F] mb-5">Praktisch</p>
              <dl className="space-y-4">
                {[
                  { icon: Clock, label: "Duur", v: training.duur },
                  { icon: Users, label: "Groepsgrootte", v: training.groep },
                  { icon: Calendar, label: "Data", v: training.data },
                  { icon: Award, label: "Certificering", v: training.certificering },
                ].map((row) => {
                  const Icon = row.icon;
                  return (
                    <div key={row.label} className="flex items-start gap-3 border-t border-[#E5E1D8] pt-4 first:border-0 first:pt-0">
                      <Icon size={16} className="text-[#E06B3F] mt-1 shrink-0" />
                      <div>
                        <dt className="text-[10px] uppercase tracking-widest text-[#7A8276]">{row.label}</dt>
                        <dd className="text-sm text-[#1D2A22] mt-0.5">{row.v}</dd>
                      </div>
                    </div>
                  );
                })}
                <div className="border-t border-[#E5E1D8] pt-4">
                  <dt className="text-[10px] uppercase tracking-widest text-[#7A8276]">Prijs</dt>
                  <dd className="text-sm text-[#1D2A22] mt-0.5">{training.prijs}</dd>
                </div>
              </dl>
              <div className="mt-6 flex flex-col gap-2">
                <a
                  href={buildPilotMailto({
                    subject: `Aanmelden training, ${training.titel}`,
                    body: `Beste LevelLeven,\n\nWij willen ons aanmelden voor de training "${training.titel}".\n\nOrganisatie:\nAantal deelnemers:\nContactpersoon + telefoon:\nGewenste startdatum:\n\nMet vriendelijke groet,`,
                  })}
                  className="group inline-flex items-center justify-between gap-3 bg-[#1D2A22] hover:bg-[#E06B3F] text-[#FAF7F1] rounded-full pl-5 pr-2 py-2 text-sm transition-colors"
                >
                  <span>Aanmelden</span>
                  <span className="bg-[#E06B3F] group-hover:bg-[#FAF7F1] group-hover:text-[#1D2A22] text-white w-8 h-8 rounded-full flex items-center justify-center transition-colors">
                    <ArrowUpRight size={14} />
                  </span>
                </a>
                <a
                  href={buildPilotMailto({
                    subject: `Brochure aanvraag, ${training.titel}`,
                    body: `Beste LevelLeven,\n\nWij ontvangen graag de brochure van "${training.titel}".\n\nOrganisatie:\nContactpersoon:\n\nMet vriendelijke groet,`,
                  })}
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-[#1D2A22] text-[#1D2A22] hover:bg-[#1D2A22] hover:text-[#FAF7F1] px-4 py-2 text-sm transition-colors"
                >
                  Download brochure
                </a>
              </div>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}
